/* eslint-disable max-len */
import React, { FC, useCallback, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import styled, { css } from 'styled-components';
import { titleCase } from 'title-case';
import { Playlists } from '../../../@resources/sounds/music';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, usePrompt } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { FlexScroller } from '../../../ui/Scrollers';
import { useResolve } from '../../Container';
import { AdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { RelativeTo } from '../../canvas/entities/Physical';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { SimpleOrigin } from '../../canvas/visualization/Origin';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Jukebox } from './Jukebox';
import { jukeboxSetTrack } from './JukeboxCommands';
import { JukeboxSnake } from './JukeboxSnake';
import { RenderTime } from './JukeboxVisual';

const XScrollbars = Scrollbars as any;

const textStyle = css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Header = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 5px;
    font-size: 10px;
    font-weight: 450;
    text-align: center;
    border-bottom: 1px solid #888888;

    svg {
        width: 12px;
        height: 12px;
    }
`;

const HeaderTitle = styled.div`
    ${textStyle}
`;

const Content = styled.div`
    flex: 1 1 auto;
    display: flex;
    padding: 2px 0;
`;

const MenuDivide = styled.div`
    width: 100%;
    padding: 0 15px;
    justify-content: space-between;
    font-size: 10px;
    font-variant-numeric: tabular-nums;

    &:before {
        inset: 0 2px !important;
        border-radius: 4px;
    }
`;

const MenuItem = styled(DropButton)`
    width: 100%;
    padding: 0 15px;
    justify-content: space-between;
    font-size: 10px;
    font-variant-numeric: tabular-nums;

    &:before {
        inset: 0 2px !important;
        border-radius: 4px;
    }
`;

// const MenuItem = styled.div`
//     display: flex;
//     justify-content: space-between;
//     font-size: 0.6rem;
//     font-variant-numeric: tabular-nums;

//     &:hover * {
//         text-decoration: underline;
//         background: #DDDDDD;
//     }
// `;

const MenuItemText = styled.div<{ current?: boolean }>`
    ${textStyle}
    color: ${p => p.current ? `${Colors.primary}` : 'inherit'};
    width: 100%;
    padding-right: 0.75rem;
`;

const MenuItemInfo = styled.div``;

const TextInfo = styled.div`
    width: 100%;
    padding: 0 15px;
    justify-content: space-between;
    font-size: 8px;
`;

export const JukeboxAdapter: FC<{ info: AdapterInfo }> = ({ info }) => {
    const objects = useResolve(ObjectManager);
    const session = useResolve(EditorSession);
    const dispatch = useResolve(CommandDispatcher);
    const subject = useMemo(() => info.type === 'jukebox' ? objects.getMaybe(info.target, Jukebox) : null, [info]);
    useComponentObserver(subject);

    const bounds = subject.bounds(RelativeTo.Model);
    const screen = subject.layout.screen;

    const { currentTrack } = subject?.getCurrentTrack();
    const currentPlaylist = subject?.playlist || '';

    const [view, setView] = useState(!!subject?.playlist ? `playlists/${subject.playlist}` : 'menu');
    const playlist = view.startsWith('playlists') ? view.split('/')[1] : '';
    const selectedPlaylist = Playlists.find(x => x.id === playlist);

    const onPlay = (p: string, index: number) => {
        dispatch(jukeboxSetTrack, [subject.id, p, index]);
        onClose();
    };

    const onClose = useCallback(() => {
        session.update(AdapterInfo, AdapterInfo.empty);
    }, [subject]);

    const promptRef = usePrompt({
        onAction: onClose,
    });

    return <SimpleOrigin>
        <Flexer absolute style={{ ...bounds.toCSS() }} pointerEvents="none">
            <Flexer flex="column" absolute style={{ ...screen.toCSS(), lineHeight: '1rem' }} pointerEvents="all" background="white" borderRadius="4px" className="blockWheel">
                <Header>
                    <DropButton overflow="0" onClick={() => setView('menu')}>
                        {view !== 'menu' && <Icon src="deqo://icons/hero/arrow-left" />}
                    </DropButton>
                    <HeaderTitle>{view.startsWith('playlists') ? selectedPlaylist.label : titleCase(view)}</HeaderTitle>
                    <DropButton overflow="0" onClick={() => onClose()}>
                        <Icon src="deqo://icons/hero/x" />
                    </DropButton>
                </Header>
                <Content>
                    {view.startsWith('playlists') &&
                        <FlexScroller>
                            {selectedPlaylist.tracks.map(t =>
                                <MenuItem key={t.id} onClick={() => onPlay(playlist, selectedPlaylist.tracks.indexOf(t))}>
                                    <MenuItemText current={currentPlaylist === playlist && t.id === currentTrack.id}>
                                        {t.label}
                                    </MenuItemText>
                                    <MenuItemInfo>{RenderTime(t.length / 1000)}</MenuItemInfo>
                                </MenuItem>


                            )}
                        </FlexScroller>
                    }
                    {view === 'menu' &&
                        <FlexScroller>
                            {Playlists.map(x => (
                                <MenuItem key={x.id} onClick={() => setView(`playlists/${x.id}`)}>
                                    <MenuItemText current={x.id === currentPlaylist}>{x.label}</MenuItemText>
                                    <MenuItemInfo>{x.tracks.length} tracks</MenuItemInfo>
                                </MenuItem>
                            ))}
                            <MenuDivide>
                                ---------------------------------
                            </MenuDivide>
                            <MenuItem onClick={() => setView('about')}>
                                <MenuItemText>About</MenuItemText>
                            </MenuItem>
                            <MenuItem onClick={() => setView('credits')}>
                                <MenuItemText>Credits</MenuItemText>
                            </MenuItem>
                            <MenuItem onClick={() => setView('snake')}>
                                <MenuItemText>Snake</MenuItemText>
                            </MenuItem>
                        </FlexScroller>
                    }
                    {view === 'about' &&
                        <TextInfo>
                            <span style={{ textDecoration: 'underline' }}>Metro Player v1</span>
                            <br />Created by Kit Lawless &amp; Steve Whitfield
                            <br />Tested by Jamie Hanratty
                            <br />Coloured in by Steven Moseley
                            <br /><span style={{ fontSize: '6px' }}>{'...and yes that\'s colour with a "u"'}</span>
                        </TextInfo>
                    }
                    {view === 'credits' &&
                        <FlexScroller>
                            <TextInfo>
                                <div>
                                    {`"Chill & Focus", "Space", "Playful & Fun", "Groove & Funk", "Dramatic", "Piano & Classical", "Ambience & Nature", "Guzheng City", 
                                    "Airport Lounge", "Covert Affair", "Beach Party", "As I Figure", "Windswept", "Loopster", "Space Jazz", "Vibing Over Venus", 
                                    "Galactic Rap", "Jellyfish in Space", "Space 1990", "Bleeping Demo", "Equatorial Complex", "Look Busy", "Onion Capers", 
                                    "Maple Leaf Rag", "Frogs Legs Rag", "Fuzzball Parade", "Beachfront Celebration", "Hillbilly Swing", "Corncob", "Fig Leaf Rag", 
                                    "Wholesome", "Carpe Diem", "Stringed Disco", "Lord of the Rangs", "Funky Chunk", "Disco con Tutti", "Cloud Dancer", "Brain Dance", 
                                    "Club Seamus", "Island Meet and Greet", "Continue Life", "Danse Morialta", "Gymnopedie No. 1", "Canon in D Major", "Dragon and Toast", 
                                    "Klockworx", "Southern Gothic", "Adding the Sun"`}
                                </div>
                                <br />Kevin MacLeod (incompetech.com)
                                <br />Licensed under Creative Commons: By Attribution 3.0
                                <br />http://creativecommons.org/licenses/by/3.0/
                            </TextInfo>
                        </FlexScroller>
                    }
                    {view === 'snake' && <JukeboxSnake />}
                </Content>
            </Flexer>
        </Flexer>
    </SimpleOrigin>;
};
